import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Table, 
  Card, 
  Button, 
  Tag, 
  Space,
  Modal,
  message,
  Tooltip,
  Input,
  Dropdown,
  Tabs,
  Descriptions
} from 'antd';
import { 
  UserOutlined, 
  PhoneOutlined,
  MailOutlined,
  SearchOutlined,
  GlobalOutlined,
  MobileOutlined,
  ShoppingOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { subscriberService } from '../../services/subscriberService';
import TableFilter, { FilterConfig } from '../../components/TableFilter/TableFilter';
import AdminResetPasswordModal from '../../components/admin/AdminResetPasswordModal';
import { voucherService } from 'services/voucherService';
import { esimProfileService, EsimProfile } from '../../services/esimProfileService';
import { transactionService, Transaction } from '../../services/transactionService';
import { subscriptionService, SubPlan } from '../../services/subscriptionService';
import moment from 'moment';





const Subscribers: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);


  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState<any>({});

  const [subscriberPacks, setSubscriberPacks] = useState([]);

  // Details modal data
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [esimProfiles, setEsimProfiles] = useState<EsimProfile[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubPlan[]>([]);


  // const { 
  //   filteredData, 
  //   handleFilterChange, 
  //   handleClearFilters 
  // } = useOptimizedPageSearch<any>({
  //   data: subscribers,
  //   searchFields: ['firstName', 'lastName', 'email', 'phoneNumber', 'status']
  // });

  const filterConfigs: FilterConfig[] = [
    {
      key: 'firstName',
      label: t('common.firstName'),
      type: 'text',
      placeholder: t('filters.searchByFirstName'),
      width: 8,
    },
    {
      key: 'lastName',
      label: t('common.lastName'),
      type: 'text',
      placeholder: t('filters.searchByLastName'),
      width: 8,
    },
    {
      key: 'email',
      label: t('common.email'),
      type: 'text',
      placeholder: t('filters.searchByEmail'),
      width: 8,
    },
    {
      key: 'phoneNum',
      label: t('common.phone'),
      type: 'text',
      placeholder: t('filters.searchByPhone'),
      width: 8,
    },
    {
    key: 'status',
    label: t('common.status'),
    type: 'multiSelect',
    options: [
      { value: 'ACTIVE', label: 'ACTIVE', color: 'green' },
      { value: 'AVAILABLE', label: 'AVAILABLE', color: 'blue' },
      { value: 'LOADED', label: 'LOADED', color: 'cyan' },
      { value: 'PRE_ACTIVATED', label: 'PRE_ACTIVATED', color: 'gold' },
      { value: 'SUSPENDED', label: 'SUSPENDED', color: 'red' },
      { value: 'OPERATOR_BARRED', label: 'OPERATOR_BARRED', color: 'volcano' },
      { value: 'TARIFF_HOLIDAY', label: 'TARIFF HOLIDAY', color: 'orange' },
      { value: 'TERMINATED', label: 'TERMINATED', color: 'magenta' },
      { value: 'TEST_MODE', label: 'TEST MODE', color: 'purple' },
    ],
    width: 8
    
  }

    
    // },
    // {
    //   key: 'createdAt',
    //   label: 'Date d\'inscription',
    //   type: 'dateRange',
    //   width: 8,
    // },
  ];

  const handleServerFilterChange = (newFilters: any) => {
  setPage(1); // reset pagination
  setFilters(newFilters);
};


  const fetchSubscribers = useCallback(async (
  pageParam = page,
  pageSizeParam = pageSize,
  filtersParam = filters
) => {
  try {
    setLoading(true);

    const response = await subscriberService.search({
      page: pageParam - 1, // Spring = 0-based
      size: pageSizeParam,
      ...filtersParam,
    });

    setSubscribers(response.data.content);
    setTotal(response.data.totalElements);
  } catch (e) {
      message.error(t('admin.subscribers.loadError'));
  } finally {
    setLoading(false);
  }
}, [page, pageSize, filters, t]);

useEffect(() => {
  fetchSubscribers(page, pageSize, filters);
}, [page, pageSize, filters, fetchSubscribers]);



  const showResetPasswordModal = (subscriber: any) => {
    setSelectedSubscriber(subscriber);
    setResetPasswordModalVisible(true);
  };

  const handleResetPasswordModalCancel = () => {
    setSelectedSubscriber(null);
    setResetPasswordModalVisible(false);
  };

  const fetchSubscriberDetails = async (subscriberId: number): Promise<void> => {
    console.log('📍 [ADMIN] Fetching details for subscriber ID:', subscriberId);
    try {
      setLoadingDetails(true);
      
      // Fetch eSIM profiles
      try {
        console.log('🔄 [ADMIN] Fetching eSIM profiles...');
        const esimResponse = await esimProfileService.getBySubscriber(subscriberId);
        console.log('✅ [ADMIN] eSIM profiles response:', esimResponse);
        const esimData = Array.isArray(esimResponse.data) ? esimResponse.data : (esimResponse.data ? [esimResponse.data] : []);
        setEsimProfiles(esimData);
        console.log('📊 [ADMIN] eSIM profiles set:', esimData);
      } catch (error) {
        console.error('❌ [ADMIN] Error fetching eSIM profiles:', error);
        setEsimProfiles([]);
      }

      // Fetch transactions
      try {
        console.log('🔄 [ADMIN] Fetching transactions...');
        const transactionsResponse = await transactionService.getBySubscriber(subscriberId);
        console.log('✅ [ADMIN] Transactions response:', transactionsResponse);
        const transactionsData = Array.isArray(transactionsResponse.data) ? transactionsResponse.data : (transactionsResponse.data?.content || transactionsResponse.data?.data || []);
        setTransactions(transactionsData);
        console.log('📊 [ADMIN] Transactions set:', transactionsData);
      } catch (error) {
        console.error('❌ [ADMIN] Error fetching transactions:', error);
        setTransactions([]);
      }

      // Fetch subscriptions
      try {
        console.log('🔄 [ADMIN] Fetching subscriptions...');
        const subscriptionsResponse = await subscriptionService.getBySubscriber(subscriberId);
        console.log('✅ [ADMIN] Subscriptions response:', subscriptionsResponse);
        const subscriptionsData = Array.isArray(subscriptionsResponse.data) ? subscriptionsResponse.data : (subscriptionsResponse.data?.content || subscriptionsResponse.data?.data || []);
        setSubscriptions(subscriptionsData);
        console.log('📊 [ADMIN] Subscriptions set:', subscriptionsData);
      } catch (error) {
        console.error('❌ [ADMIN] Error fetching subscriptions:', error);
        setSubscriptions([]);
      }

      // Fetch vouchers/packs
      try {
        console.log('🔄 [ADMIN] Fetching vouchers...');
        const response = await voucherService.getBySubscriber(subscriberId);
        console.log('✅ [ADMIN] Vouchers response:', response);
        const vouchersData = Array.isArray(response.data) ? response.data : (response.data?.content || response.data?.data || []);
        setSubscriberPacks(vouchersData);
        console.log('📊 [ADMIN] Vouchers set:', vouchersData);
      } catch (error) {
        console.error('❌ [ADMIN] Error fetching vouchers:', error);
        setSubscriberPacks([]);
      }
    } catch (error) {
      console.error('❌ [ADMIN] Global error fetching subscriber details:', error);
      message.error(t('admin.subscribers.detailsLoadError'));
    } finally {
      setLoadingDetails(false);
      console.log('✅ [ADMIN] Finished loading subscriber details');
    }
  };

  // const renderActionMenu = (record: any) => (
  //   <Menu>
  //     <Menu.Item 
  //       key="details" 
  //       onClick={() => {
  //         setSelectedSubscriber(record);
  //         setDetailsVisible(true);
  //       }}
  //     >
  //       <UserOutlined /> Détails
  //     </Menu.Item>
  //     {/*<Menu.Item key="resetPassword" onClick={() => showResetPasswordModal(record)}>
  //       <LockOutlined /> Réinitialiser le mot de passe
  //     </Menu.Item>*/}
  //   </Menu>
  // );


 


  const renderActionMenu = (record: any) => ({
  items: [
    {
      key: 'details',
      label: (
        <span
           onClick={async (e) => {
            e.preventDefault();
            setSelectedSubscriber(record);
            setDetailsVisible(true);
            fetchSubscriberDetails(record.id);
          }}
        >
          <UserOutlined /> Détails
        </span>
      ),
    },
  ],
});


  const handleStatusChange = async (id: number, status: string) => {
    try {
      await subscriberService.updateStatus(id, status);
      message.success(t('admin.subscribers.statusUpdateSuccess'));
      fetchSubscribers();
    } catch (error) {
      message.error(t('admin.subscribers.statusUpdateError'));
    }
  };

  const columns = [
    {
      title: t('common.fullName'),
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: (a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('filters.searchByName')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => `${record.firstName} ${record.lastName}`.toLowerCase().includes(value.toLowerCase()),
      render: (_, record) => (
        <Space>
          <UserOutlined />
          {`${record.firstName} ${record.lastName}`}
        </Space>
      ),
    },
    {
      title: t('common.email'),
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('filters.searchByEmail')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.email.toLowerCase().includes(value.toLowerCase()),
      render: (email) => (
        <Tooltip title={email}>
          <Space>
            <MailOutlined />
            {email}
          </Space>
        </Tooltip>
      ),
    },
    {
      title: t('common.phone'),
      dataIndex: 'phoneNum',
      key: 'phoneNum',
      sorter: (a, b) => a.phoneNum.localeCompare(b.phoneNum),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('filters.searchByPhone')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.phoneNum.toLowerCase().includes(value.toLowerCase()),
      render: (phone) => (
        <Space>
          <PhoneOutlined />
          {phone}
        </Space>
      ),
    },
    {
      title: t('common.country'),
      dataIndex: 'country',
      key: 'country',
      sorter: (a, b) => (a.country.country || '').localeCompare(b.country.country || ''),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('filters.searchByCountry')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => (record.country.country || '').toLowerCase().includes(value.toLowerCase()),
      render: (country) => (
        <Space>
          <GlobalOutlined />
          {country.country || '-'}
        </Space>
      ),
    },

    {
      title: t('common.status'),
      key: 'status',
      dataIndex: 'status',
      sorter: (a, b) => a.status.localeCompare(b.status),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('filters.searchByStatus')}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('common.search')}
            </Button>
            <Button onClick={() => clearFilters()} size="small">
              {t('common.reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.status.toLowerCase().includes(value.toLowerCase()),
     render: (status) => {
        // Couleurs selon toutes les options
        const colorMap: Record<string, string> = {
          ACTIVE: 'green',
          AVAILABLE: 'blue',
          LOADED: 'cyan',
          PRE_ACTIVATED: 'gold',
          SUSPENDED: 'red',
          OPERATOR_BARRED: 'volcano',
          TARIFF_HOLIDAY: 'orange',
          TERMINATED: 'magenta',
          TEST_MODE: 'purple',
        
        };
    const color = colorMap[status] || 'default';
    return <Tag color={color}>{status}</Tag>;
  },
    },
    {
    title: t('common.actions'),
    key: 'actions',
    render: (_, record) => (
      <Dropdown
        menu={renderActionMenu(record)}
        trigger={['click']}
      >
        <Button type="link" icon={<SearchOutlined style={{ fontSize: 22, color: '#09b7fcff' }} />} size="small" />
      </Dropdown>
    ),
  }

  ];




  return (
    <>
      <TableFilter
          filters={filterConfigs}
          onFilterChange={handleServerFilterChange}
          onClearFilters={() => setFilters({})}
        />

      <Card title={`${t('admin.subscribers.cardTitle')} (${subscribers.length})`}>
       <Table
            columns={columns}
            dataSource={subscribers}
            loading={loading}
            rowKey="id"
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              onChange: (newPage, newPageSize) => {
                setPage(newPage);        
                setPageSize(newPageSize); 
              }
            }}
          />


      </Card>

      <Modal
        title={selectedSubscriber ? `${t('admin.subscribers.detailsModal')}: ${selectedSubscriber.firstName} ${selectedSubscriber.lastName}` : t('admin.subscribers.detailsModal')}
        visible={detailsVisible}
        onCancel={() => {
          setDetailsVisible(false);
          setSelectedSubscriber(null);
          setEsimProfiles([]);
          setTransactions([]);
          setSubscriptions([]);
          setSubscriberPacks([]);
        }}
        footer={null}
        width={1000}
      >
        {selectedSubscriber && (
          <>
            <Descriptions bordered column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('common.firstName')}>{selectedSubscriber.firstName}</Descriptions.Item>
              <Descriptions.Item label={t('common.lastName')}>{selectedSubscriber.lastName}</Descriptions.Item>
              <Descriptions.Item label={t('common.email')}>{selectedSubscriber.email}</Descriptions.Item>
              <Descriptions.Item label={t('common.phone')}>{selectedSubscriber.phoneNum}</Descriptions.Item>
              <Descriptions.Item label={t('common.address')}>{selectedSubscriber.address || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('common.city')}>{selectedSubscriber.city || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('common.country')}>{selectedSubscriber.country?.country || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('common.status')}>
                <Tag color={
                  selectedSubscriber.status === 'ACTIVE' ? 'green' :
                  selectedSubscriber.status === 'SUSPENDED' ? 'red' :
                  selectedSubscriber.status === 'AVAILABLE' ? 'blue' :
                  selectedSubscriber.status === 'LOADED' ? 'cyan' :
                  selectedSubscriber.status === 'PRE_ACTIVATED' ? 'gold' :
                  selectedSubscriber.status === 'OPERATOR_BARRED' ? 'volcano' :
                  selectedSubscriber.status === 'TARIFF_HOLIDAY' ? 'orange' :
                  selectedSubscriber.status === 'TERMINATED' ? 'magenta' :
                  selectedSubscriber.status === 'TEST_MODE' ? 'purple' : 'default'
                }>
                  {selectedSubscriber.status}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Tabs 
              defaultActiveKey="esimProfiles" 
              style={{ marginTop: 16 }}
              items={[
                {
                  key: 'esimProfiles',
                  label: (
                    <span>
                      <MobileOutlined />
                      {t('admin.subscribers.esimProfiles')}
                    </span>
                  ),
                  children: (
                    <Table<EsimProfile>
                      columns={[
                        {
                          title: t('esim.iccid'),
                          dataIndex: 'simSerial',
                          key: 'iccid',
                        },
                        {
                          title: t('esim.msisdn'),
                          dataIndex: 'msisdn',
                          key: 'msisdn',
                        },
                        {
                          title: t('common.status'),
                          dataIndex: 'status',
                          key: 'status',
                          render: (status: string) => (
                            <Tag color={
                              status === 'ACTIVE' ? 'green' :
                              status === 'INACTIVE' ? 'red' :
                              status === 'SUSPENDED' ? 'orange' : 'default'
                            }>
                              {status}
                            </Tag>
                          ),
                        },
                        {
                          title: t('esim.dataUsed'),
                          dataIndex: 'dataUsed',
                          key: 'dataUsed',
                          render: (dataUsed: number) => `${(dataUsed / 1024 / 1024).toFixed(2)} MB`,
                        },
                        {
                          title: t('esim.dataLimit'),
                          dataIndex: 'dataLimit',
                          key: 'dataLimit',
                          render: (dataLimit: number) => `${(dataLimit / 1024 / 1024).toFixed(2)} MB`,
                        },
                        {
                          title: t('esim.activationDate'),
                          dataIndex: 'activationDate',
                          key: 'activationDate',
                          render: (date: Date) => date ? moment(date).format('DD/MM/YYYY HH:mm') : '-',
                        },
                        {
                          title: t('esim.expirationDate'),
                          dataIndex: 'expirationDate',
                          key: 'expirationDate',
                          render: (date: Date) => date ? moment(date).format('DD/MM/YYYY HH:mm') : '-',
                        },
                      ]}
                      dataSource={esimProfiles}
                      loading={loadingDetails}
                      rowKey="id"
                      pagination={false}
                    />
                  ),
                },
                {
                  key: 'transactions',
                  label: (
                    <span>
                      <ShoppingOutlined />
                      {t('admin.subscribers.purchases')}
                    </span>
                  ),
                  children: (
                    <Table<Transaction>
                      columns={[
                        {
                          title: t('transactions.id'),
                          dataIndex: 'id',
                          key: 'id',
                        },
                        {
                          title: t('transactions.type'),
                          dataIndex: 'type',
                          key: 'type',
                          render: (type: string) => (
                            <Tag>{type}</Tag>
                          ),
                        },
                        {
                          title: t('transactions.amount'),
                          dataIndex: 'amount',
                          key: 'amount',
                          render: (amount: number, record: Transaction) => `${amount} ${record.currency}`,
                        },
                        {
                          title: t('common.status'),
                          dataIndex: 'status',
                          key: 'status',
                          render: (status: string) => (
                            <Tag color={
                              status === 'COMPLETED' ? 'green' :
                              status === 'PENDING' ? 'gold' :
                              status === 'FAILED' ? 'red' : 'default'
                            }>
                              {status}
                            </Tag>
                          ),
                        },
                        {
                          title: t('transactions.description'),
                          dataIndex: 'description',
                          key: 'description',
                        },
                        {
                          title: t('transactions.date'),
                          dataIndex: 'createdAt',
                          key: 'createdAt',
                          render: (date: Date) => moment(date).format('DD/MM/YYYY HH:mm'),
                        },
                      ]}
                      dataSource={transactions}
                      loading={loadingDetails}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                    />
                  ),
                },
                {
                  key: 'subscriptions',
                  label: (
                    <span>
                      <DatabaseOutlined />
                      {t('admin.subscribers.packsConsumptions')}
                    </span>
                  ),
                  children: (
                    <Table<SubPlan>
                      columns={[
                        {
                          title: t('subscriptions.id'),
                          dataIndex: 'id',
                          key: 'id',
                        },
                        {
                          title: t('subscriptions.packName'),
                          key: 'packName',
                          render: (_, record: SubPlan) => record.pack?.name || '-',
                        },
                        {
                          title: t('subscriptions.iccidSimSerial'),
                          key: 'simSerial',
                          render: (_, record: SubPlan) => record.esimProfile?.simSerial || record.esimProfile?.eid || '-',
                        },
                        {
                          title: t('common.status'),
                          key: 'esimStatus',
                          render: (_, record: SubPlan) => {
                            const status = record.esimProfile?.status;
                            if (!status) return <Tag>N/A</Tag>;
                            return (
                              <Tag color={
                                status === 'ENABLED' ? 'green' :
                                status === 'ALLOCATED' ? 'blue' :
                                status === 'SUSPENDED' ? 'orange' :
                                status === 'RELEASED' ? 'red' : 'default'
                              }>
                                {status}
                              </Tag>
                            );
                          },
                        },
                        {
                          title: t('subscriptions.dataLimit'),
                          key: 'dataLimit',
                          render: (_, record: SubPlan) => {
                            const pack = record.pack;
                            if (!pack?.dataValue || !pack?.dataUnit) return '-';
                            return `${pack.dataValue} ${pack.dataUnit}`;
                          },
                        },
                        {
                          title: t('subscriptions.startDate'),
                          dataIndex: 'startingDate',
                          key: 'startingDate',
                          render: (date: string) => date ? moment(date).format('DD/MM/YYYY') : '-',
                        },
                        {
                          title: t('subscriptions.endDate'),
                          dataIndex: 'endingDate',
                          key: 'endingDate',
                          render: (date: string) => date ? moment(date).format('DD/MM/YYYY') : '-',
                        },
                      ]}
                      dataSource={subscriptions}
                      loading={loadingDetails}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                    />
                  ),
                },
                {
                  key: 'vouchers',
                  label: (
                    <span>
                      <ShoppingOutlined />
                      {t('admin.subscribers.vouchers')}
                    </span>
                  ),
                  children: (
                    <Table
                      dataSource={subscriberPacks}
                      loading={loadingDetails}
                      columns={[
                        {
                          title: t('common.pack'),
                          dataIndex: 'packId',
                        },
                        {
                          title: t('common.amount'),
                          dataIndex:'amount',
                          render: (amount) => amount?.toFixed(2) || '0.00',
                        },
                        {
                          title: t('common.startDate'),
                          dataIndex: 'creationDateTime',
                          render: (date) => date ? new Date(date).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          }) : '-',
                        },
                        {
                          title: t('common.endDate'),
                          dataIndex: 'expirationDateTime',
                          render: (date) => date ? new Date(date).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          }) : '-',
                        },
                        {
                          title: t('common.status'),
                          dataIndex: 'status',
                          render: (status) => {
                            const colorMap: Record<string, string> = {
                              ACTIVE: 'green',
                              SUSPENDED: 'red',
                              TERMINATED: 'magenta',
                              AVAILABLE: 'blue',
                              LOADED: 'cyan',
                              PRE_ACTIVATED: 'gold',
                              OPERATOR_BARRED: 'volcano',
                              TARIFF_HOLIDAY: 'orange',
                              TEST_MODE: 'purple',
                              REDEEMED:'orange'
                            };
                            const color = colorMap[status] || 'default';
                            return <Tag color={color}>{status}</Tag>;
                          },
                        }
                      ]}
                      pagination={false}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>

      {/* Modal pour réinitialiser le mot de passe */}
      <AdminResetPasswordModal
        visible={resetPasswordModalVisible}
        onCancel={handleResetPasswordModalCancel}
        userId={selectedSubscriber?.id}
        userEmail={selectedSubscriber?.email}
        userType="subscriber"
      />
    </>
  );
};

export default Subscribers;